// src/userPanel/components/ClimaWidget.jsx
import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'clima_widget_expandido';

export default function ClimaWidget({ usuario, onChangeLocation, tieneMascotas = false }) {
  const [climaData, setClimaData] = useState(null);
  // Inicializar estado: preferencia guardada, o expandido en desktop / colapsado en móvil
  const [expandido, setExpandido] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) return saved === 'true';
    // Default: colapsado en móvil, expandido en desktop
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });
  const [cargando, setCargando] = useState(true);
  const [, setError] = useState(null);
  const [errorCiudad, setErrorCiudad] = useState(false);

  // Guardar preferencia cuando cambia
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(expandido));
  }, [expandido]);

  useEffect(() => {
    cargarClima();
  }, [usuario?.id, usuario?.ubicacion?.ciudad, usuario?.ubicacion?.configurada]);

  const cargarClima = async () => {
    if (!usuario?.id) return;

    setCargando(true);
    setError(null);
    setErrorCiudad(false);

    const url = `/api/usuarios/${usuario.id}/clima`;
    logger.debug('Clima - Solicitando:', url);

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('nfcToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      logger.debug('Clima - Respuesta:', result);

      // Verificar si es error de ciudad no encontrada
      if (!response.ok) {
        if (response.status === 404 && result.error === 'CIUDAD_NO_ENCONTRADA') {
          logger.debug('Clima - Ciudad no encontrada');
          setErrorCiudad(true);
          setClimaData(null);
          return;
        }
        throw new Error(result.mensaje || 'Error al obtener clima');
      }

      if (result.success && result.data) {
        setClimaData(result.data);
      } else {
        // Usuario sin ubicación configurada
        setClimaData(null);
      }
    } catch (err) {
      logger.error('Clima - Error:', err);
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // ✅ CORREGIDO: Mostrar estado de carga en lugar de desaparecer
  if (!climaData) {
    if (cargando) {
      return (
        <div
          style={{
            backgroundColor: 'var(--white)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2) var(--space-3)',
            border: `1px solid var(--gray-200)`,
            textAlign: 'center',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--gray-600)'
          }}
        >
          <i className="bi bi-hourglass-split me-2"></i>
          Cargando clima...
        </div>
      );
    }

    // Si hay error de ciudad no encontrada, mostrar mensaje con opción
    if (errorCiudad) {
      return (
        <div
          style={{
            backgroundColor: 'var(--white)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            border: '1px solid var(--color-warning)',
            fontSize: 'var(--font-size-sm)'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-2)'
          }}>
            <span style={{ fontSize: 'var(--font-size-base)' }}>⚠️</span>
            <span style={{ color: 'var(--gray-700)' }}>
              No pudimos obtener el clima para tu ubicación
            </span>
          </div>
          {onChangeLocation && (
            <button
              onClick={onChangeLocation}
              style={{
                background: 'none',
                border: '1px solid var(--gray-400)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--gray-700)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)'
              }}
            >
              <i className="bi bi-geo-alt"></i>
              Cambiar ubicación
            </button>
          )}
        </div>
      );
    }

    // Si hay error o no hay datos (sin ubicación), no mostrar nada
    return null;
  }

  const { ubicacion, clima, consejos } = climaData;

  return (
    <div 
      style={{
        backgroundColor: 'var(--white)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2) var(--space-3)',
        boxShadow: expandido ? 'var(--shadow-lg)' : 'none',
        border: `1px solid var(--gray-200)`,
        transition: 'var(--transition-base)'
      }}
    >
      {/* Versión compacta */}
      <div 
        onClick={() => setExpandido(!expandido)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          gap: 'var(--space-2)'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flex: 1
        }}>
          <span style={{ fontSize: 'var(--font-size-xl)' }}>
            🌤️
          </span>
          <div style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--gray-700)',
            lineHeight: '1.2'
          }}>
            <strong>{ubicacion.ciudad}</strong>: {clima.temperatura}°C - {clima.estado}
          </div>
        </div>
        
        <button
          style={{
            background: 'none',
            border: 'none',
            padding: 'var(--space-1)',
            cursor: 'pointer',
            color: 'var(--gray-600)',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setExpandido(!expandido);
          }}
        >
          {expandido ? 'Ocultar' : 'Ver más'}
          <i className={`bi bi-chevron-${expandido ? 'up' : 'down'}`}></i>
        </button>
      </div>

      {/* Versión expandida */}
      {expandido && (
        <div 
          style={{
            marginTop: 'var(--space-3)',
            paddingTop: 'var(--space-3)',
            borderTop: `1px solid var(--gray-200)`
          }}
        >
          {/* Detalles del clima */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)'
          }}>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              <strong style={{ color: 'var(--gray-700)' }}>Sensación:</strong>
              <br />
              {clima.sensacion}°C
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              <strong style={{ color: 'var(--gray-700)' }}>Humedad:</strong>
              <br />
              {clima.humedad}%
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              <strong style={{ color: 'var(--gray-700)' }}>Viento:</strong>
              <br />
              {clima.viento}
            </div>
          </div>

          {/* Consejos - Solo mostrar si tiene mascotas */}
          {tieneMascotas && consejos && consejos.length > 0 && (
            <div>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--gray-900)',
                marginBottom: 'var(--space-2)'
              }}>
                💡 Tips para hoy:
              </div>
              <ul style={{
                margin: 0,
                paddingLeft: 'var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--gray-700)',
                lineHeight: '1.6'
              }}>
                {consejos.map((consejo, index) => (
                  <li key={index} style={{ marginBottom: 'var(--space-1)' }}>
                    {consejo}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}